import React, { useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { ENV } from '../../../config/env';
import toast from 'react-hot-toast';

interface Review {
  rating: number;
  title: string;
  content: string;
  pros: string[];
  cons: string[];
  reviewer: string;
  verified: boolean;
}

const ProductReviewTab: React.FC = () => {
  const { user, getToken } = useAuth();
  const [loading, setLoading] = useState(false);
  const [productName, setProductName] = useState('');
  const [rating, setRating] = useState(4);
  const [reviewCount, setReviewCount] = useState(3);
  const [reviewLength, setReviewLength] = useState('medium');
  const [reviews, setReviews] = useState<Review[]>([]);

  const handleGenerate = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!productName.trim()) {
      toast.error('Please enter a product name');
      return;
    }

    try {
      setLoading(true);
      toast.loading('Generating reviews...');

      const userId = await getToken();
      const response = await fetch(`${ENV.API_URL}/api/marketing/product-review`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId, productName, rating, reviewCount, reviewLength })
      });

      if (!response.ok) throw new Error('Failed to generate reviews');

      const data = await response.json();
      toast.dismiss();
      setReviews(data.reviews);
      toast.success(`Generated ${data.reviews.length} reviews!`);
    } catch (error) {
      toast.dismiss();
      console.error('Error:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to generate reviews');
    } finally {
      setLoading(false);
    }
  };

  const renderStars = (rating: number) => {
    return (
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map(star => (
          <svg
            key={star}
            className={`w-5 h-5 ${star <= rating ? 'text-yellow-400 fill-current' : 'text-gray-300'}`}
            viewBox="0 0 20 20"
          >
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
          </svg>
        ))}
      </div>
    );
  };

  return (
    <div>
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Product Review Generator</h3>
        <p className="text-gray-600 text-sm">
          Generate authentic-sounding product reviews with ratings, pros, cons, and varied reviewer personas.
        </p>
      </div>

      {reviews.length === 0 ? (
        <form onSubmit={handleGenerate} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Product Name
            </label>
            <input
              type="text"
              value={productName}
              onChange={(e) => setProductName(e.target.value)}
              placeholder="e.g., Wireless Bluetooth Headphones XPro"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Average Rating
              </label>
              <select
                value={rating}
                onChange={(e) => setRating(Number(e.target.value))}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              >
                <option value={5}>5 Stars (Excellent)</option>
                <option value={4}>4 Stars (Very Good)</option>
                <option value={3}>3 Stars (Good)</option>
                <option value={2}>2 Stars (Fair)</option>
                <option value={1}>1 Star (Poor)</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Number of Reviews
              </label>
              <select
                value={reviewCount}
                onChange={(e) => setReviewCount(Number(e.target.value))}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              >
                <option value={1}>1 Review</option>
                <option value={3}>3 Reviews</option>
                <option value={5}>5 Reviews</option>
                <option value={10}>10 Reviews</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Review Length
              </label>
              <select
                value={reviewLength}
                onChange={(e) => setReviewLength(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              >
                <option value="short">Short (50-100 words)</option>
                <option value="medium">Medium (100-200 words)</option>
                <option value="long">Long (200-300 words)</option>
              </select>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className={`px-6 py-3 rounded-lg font-medium text-white transition ${
              loading ? 'bg-orange-400 cursor-not-allowed' : 'bg-orange-600 hover:bg-orange-700'
            }`}
          >
            {loading ? 'Generating...' : 'Generate Reviews'}
          </button>
        </form>
      ) : (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <h4 className="text-lg font-semibold text-gray-900">
              {reviews.length} Generated Reviews
            </h4>
            <button
              onClick={() => setReviews([])}
              className="text-sm text-orange-600 hover:text-orange-700 font-medium"
            >
              Generate New
            </button>
          </div>

          {reviews.map((review, index) => (
            <div key={index} className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-md transition-shadow">
              {/* Header */}
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    {renderStars(review.rating)}
                    <span className="text-sm font-medium text-gray-900">{review.reviewer}</span>
                    {review.verified && (
                      <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs font-medium">
                        Verified Purchase
                      </span>
                    )}
                  </div>
                  <h5 className="font-semibold text-gray-900">{review.title}</h5>
                </div>
                <button
                  onClick={() => {
                    const text = `${review.title}\n\n${review.content}\n\nPros:\n${review.pros.map(p => `• ${p}`).join('\n')}\n\nCons:\n${review.cons.map(c => `• ${c}`).join('\n')}`;
                    navigator.clipboard.writeText(text);
                    toast.success('Review copied!');
                  }}
                  className="text-sm text-orange-600 hover:text-orange-700 font-medium"
                >
                  Copy
                </button>
              </div>

              {/* Content */}
              <p className="text-gray-700 mb-4">{review.content}</p>

              {/* Pros & Cons */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {review.pros.length > 0 && (
                  <div>
                    <h6 className="text-sm font-semibold text-gray-900 mb-2">Pros:</h6>
                    <ul className="space-y-1">
                      {review.pros.map((pro, i) => (
                        <li key={i} className="text-sm text-gray-700 flex items-start">
                          <span className="text-green-600 mr-2">✓</span>
                          {pro}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {review.cons.length > 0 && (
                  <div>
                    <h6 className="text-sm font-semibold text-gray-900 mb-2">Cons:</h6>
                    <ul className="space-y-1">
                      {review.cons.map((con, i) => (
                        <li key={i} className="text-sm text-gray-700 flex items-start">
                          <span className="text-red-600 mr-2">✗</span>
                          {con}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          ))}

          {/* Export All Button */}
          <div className="flex justify-end">
            <button
              onClick={() => {
                const allReviews = reviews.map(r => 
                  `${r.rating} Stars - ${r.title}\n${r.content}\n\nPros: ${r.pros.join(', ')}\nCons: ${r.cons.join(', ')}\n\n---\n\n`
                ).join('');
                navigator.clipboard.writeText(allReviews);
                toast.success('All reviews copied!');
              }}
              className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 text-sm font-medium"
            >
              Copy All Reviews
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductReviewTab;
